import React from "react";
import ReactHtmlParser from "react-html-parser";
import ReactToPrint from "react-to-print";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";

// Karsten
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Scrollbar from "components/Scrollbar/Scrollbar";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import useLogs from "hooks/logs";

const useStyles = makeStyles({
  formControlRoot: {
    marginLeft: 9,
    "& > span": {
      fontSize: ".875rem",
    },
  },
});

export default function AdhesionTermsDialog({ open, dataStore, handleClose }) {
  const classes = useStyles();
  const { setLocalStorageApiProblem } = useLogs();
  const [acceptedTerms, setAcceptedTerms] = React.useState(
    dataStore ? dataStore.Situacao === "Aderido" : false
  );
  const snackbar = useSnackbar();
  const [confirmando, setConfirmando] = React.useState(false);
  const [termosAdesao, setTermosAdesao] = React.useState(null);
  const [buscandoTermos, setBuscandoTermos] = React.useState(false);
  const [userIp, setUserIp] = React.useState(null);
  const termsRef = React.useRef(null);

  const isMobile400 = isMobile400();

  const fetchAdhesionTerms = React.useCallback(async () => {
    setBuscandoTermos(true);

    await Api.get(
      "term-of-sale-mode",
      "storeadditionalinfo",
      "?idModalidade=" +
        dataStore.IdModalidade +
        "&idLoja=" +
        dataStore.IdPessoaLoja,
      true
    )
      .then((res) => {
        if (res.Success) {
          // para testar com um html string localmente
          // res.Result.Html = termosAdesaoBackup;

          setTermosAdesao(res.Result);
        }
      })
      .catch((error) => {
        console.log(error);
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "128",
          screen: "Termos de Adesão",
          problem: `Erro na chamada do endpoint term-of-sale-mode - ${error}`,
        });
      })
      .finally(() => setBuscandoTermos(false));
  }, [dataStore.IdModalidade, dataStore.IdPessoaLoja, snackbar]);

  const handleTermsChange = React.useCallback((event) => {
    setAcceptedTerms(event.target.checked);
  }, []);

  const handleCancel = React.useCallback(() => {
    handleClose();
  }, [handleClose]);

  const printTerms = React.useCallback(() => {
    if (termosAdesao && termosAdesao.Html) {
      const win = window.open("", "_self");
      win.document.write(termosAdesao.Html);
      win.print();
      win.close();
    }
  }, [termosAdesao]);

  const submitAccept = React.useCallback(async () => {
    setConfirmando(true);

    await Api.postParams(
      "create-sales-mode-accept",
      "storeadditionalinfo",
      "?idLoja=" +
        dataStore.IdPessoaLoja +
        "&idTermo=" +
        termosAdesao?.IdTermo +
        "&ip=" +
        userIp,
      true
    )
      .then((res) => {
        if (res.Success) {
          handleClose(res.Result);
          snackbar.showMessage(
            "Aceite dos Termos de Adesão realizado com sucesso!",
            {
              severity: "success",
            }
          );
        }
      })
      .catch((error) => {
        console.log(error);
        snackbar.showMessage(error, {
          severity: "error",
        });
        setConfirmando(false);
        setLocalStorageApiProblem({
          idMenuAccess: "128",
          screen: "Termos de Adesão",
          problem: `Erro na chamada do endpoint create-sales-mode-accept - ${error}`,
        });
      });
  }, [
    dataStore.IdPessoaLoja,
    handleClose,
    snackbar,
    termosAdesao?.IdTermo,
    userIp,
  ]);

  const submitGiveUp = React.useCallback(async () => {
    setConfirmando(true);

    await Api.postParams(
      "update-sales-mode-not-accept",
      "storeadditionalinfo",
      "?idLoja=" +
        dataStore.IdPessoaLoja +
        "&idTermo=" +
        termosAdesao?.IdTermo +
        "&ip=" +
        userIp,
      true
    )
      .then((res) => {
        if (res.Success) {
          handleClose(res.Result);
          snackbar.showMessage(
            "Desistência dos Termos de Adesão realizada com sucesso!",
            {
              severity: "success",
            }
          );
        }
      })
      .catch((error) => {
        console.log(error);
        snackbar.showMessage(error, {
          severity: "error",
        });
        setConfirmando(false);
        setLocalStorageApiProblem({
          idMenuAccess: "128",
          screen: "Termos de Adesão",
          problem: `Erro na chamada do endpoint update-sales-mode-not-accept - ${error}`,
        });
      });
  }, [
    dataStore.IdPessoaLoja,
    handleClose,
    snackbar,
    termosAdesao?.IdTermo,
    userIp,
  ]);

  React.useEffect(() => {
    UtilsFncs.GetIp((ip) => {
      if (ip) {
        setUserIp(ip);
      }
    });
  }, []);

  React.useEffect(() => {
    if (dataStore.Situacao) fetchAdhesionTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={handleCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <span
          style={{
            display: "flex",
            flexDirection: isMobile400 ? "column" : "row",
            alignItems: "center",
            justifyContent: isMobile400 ? "center" : "space-between",
          }}
        >
          <Typography variant="h3" component="h3">
            Termos de Adesão
          </Typography>

          <ReactToPrint
            trigger={() => (
              <Button
                onClick={printTerms}
                variant="outlined"
                color="primary"
                size="small"
                disabled={buscandoTermos}
              >
                Imprimir Termos de Adesão
              </Button>
            )}
            content={() => termsRef.current}
          />
        </span>
      </DialogTitle>
      <DialogContent
        style={{
          overflowY: "hidden",
          padding: "0 1.5rem",
          marginTop: "0.5rem",
        }}
      >
        {buscandoTermos || !termosAdesao ? (
          "Carregando termos de adesão"
        ) : (
          <Scrollbar
            style={{
              maxHeight: 400,
              overflowX: "hidden",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "0 10%" }} ref={termsRef}>
                {ReactHtmlParser(termosAdesao.Html)}
              </div>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={handleTermsChange}
                    color="primary"
                    disabled={dataStore.Situacao === "Aderido"}
                  />
                }
                label="Li e aceito os termos de adesão acima."
                labelPlacement="end"
                classes={{
                  root: classes.formControlRoot,
                }}
              />
            </div>
          </Scrollbar>
        )}
      </DialogContent>
      <DialogActions
        style={{
          display: "flex",
          justifyContent: isMobile400 ? "center" : "flex-end",
          flexDirection: isMobile400 ? "column" : "row",
        }}
      >
        <Button
          style={{ width: isMobile400 && "100%" }}
          component={Box}
          onClick={handleCancel}
          disabled={confirmando}
        >
          Cancelar
        </Button>
        {dataStore.Situacao !== "Aderido" ? (
          <Button
            style={{ width: isMobile400 && "100%" }}
            component={Box}
            variant="contained"
            onClick={submitAccept}
            disabled={confirmando || !acceptedTerms || buscandoTermos}
          >
            {confirmando ? "Salvando Aceite..." : "Confirmar"}
          </Button>
        ) : (
          <Button
            component={Box}
            variant="contained"
            onClick={submitGiveUp}
            disabled={buscandoTermos || confirmando}
          >
            {confirmando ? "Salvando Desistência..." : "Desistir Termos"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
